import { ForgotPasswordForm, ResetPasswordForm, useBrandex } from "@brandex/dashboard";
import { Alert, Button, Logo } from "@brandex/ui";
import type { ReactNode } from "react";

/** The centred card the admin's signed-out pages share. */
export function AuthCard({ title, lead, children }: { title: string; lead?: string; children: ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="glass rounded-[var(--radius-xl)] p-8">
          <h1 className="mb-1 font-display text-2xl font-bold">{title}</h1>
          {lead !== undefined ? <p className="mb-6 text-sm text-muted-foreground">{lead}</p> : null}
          {children}
        </div>
      </div>
    </div>
  );
}

/** `?user=&token=` from an emailed link, when well formed. */
export function linkParams(search: string): { userId: string; token: string } | null {
  const p = new URLSearchParams(search);
  const userId = p.get("user");
  const token = p.get("token");
  return userId !== null && token !== null && /^[0-9a-f]{24}$/.test(userId) ? { userId, token } : null;
}

/**
 * Staff invite (`setup`) and password reset (`reset`) links open here, not in
 * the doctor web app (ADR 0022). Success leads to the staff sign-in.
 */
export function PasswordLinkPage({ mode }: { mode: "setup" | "reset" }) {
  const link = linkParams(window.location.search);
  return (
    <AuthCard
      title={mode === "setup" ? "Choose your password" : "Choose a new password"}
      lead={mode === "setup" ? "Welcome to the BrandEx team. After this you sign in and set up your authenticator app." : undefined}
    >
      {link === null ? (
        <Alert tone="error">This link is incomplete or has expired. Ask a super admin to send a new invite, or request a new reset link.</Alert>
      ) : (
        <ResetPasswordForm userId={link.userId} token={link.token} mode={mode} />
      )}
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const { nav } = useBrandex();
  return (
    <AuthCard title="Reset your password" lead="We email a link to staff accounts. It opens this panel.">
      <ForgotPasswordForm />
      <Button variant="ghost" size="sm" className="mt-4" onClick={() => nav.navigate("/login")}>Back to sign in</Button>
    </AuthCard>
  );
}
